import { useRef, useState } from 'react';
import { useIntl } from 'umi';

import { DBSystemCode } from '@home/_config/config';
import { Permission, Status, URL_COLLECTION_ANALYSIS_FAILURE_REASON } from '@home/enums/urlCollection';
import { analysisUrlInfo } from '@home/utils/urlCollection';
import {
  getUrlCollectionPrimaryEntityType,
  normalizeUrlCollectionSystemCode,
  reindexUrlCollectionItems,
  stringifyUrlCollectionData,
} from '@home/utils/urlCollectionData';

import {
  formatShortcutPageName,
  logUrlCollectionButtonClick,
  showShortcutNotification,
  URL_COLLECTION_LOG_CONFIG,
} from './utils';
import {
  getDuplicateShortcutNameKey,
  getDuplicateShortcutUrlKey,
  SHORTCUT_NAME_MAX_LENGTH,
} from '../utils/shortcutRules';

import type { IAnalysisUrlInfoResult } from '@home/enums/urlCollection';
import type { IUrlCollectionItem } from '@home/types/urlCollection';

interface IUseShortcutImportOptions {
  draftShortcuts: IUrlCollectionItem[];
  maxCount: number;
  onDraftShortcutsChange: (items: IUrlCollectionItem[]) => void;
  onImportConfirmed: () => void;
}

interface IUseShortcutImportReturn {
  url: string;
  pageName: string;
  analysisResult: IAnalysisUrlInfoResult | null;
  importLoading: boolean;
  confirmLoading: boolean;
  shortcutImportErrorText: string;
  resetImportState: () => void;
  handleImport: () => Promise<void>;
  handleAnalyseConfirm: () => Promise<void>;
  handleUrlChange: (value: string) => void;
  handlePageNameChange: (value: string) => void;
}

const TEMP_SHORTCUT_ID_PREFIX = 'temp-shortcut-';

const createShortcutDraftItem = (
  draftShortcuts: IUrlCollectionItem[],
  nextName: string,
  analysisResult: IAnalysisUrlInfoResult,
  systemCode: IUrlCollectionItem['system'] | '',
): IUrlCollectionItem => {
  return {
    id: `${TEMP_SHORTCUT_ID_PREFIX}${Date.now()}`,
    system: systemCode || DBSystemCode.Home,
    url: analysisResult.page_url || analysisResult.page_path || '',
    name: nextName,
    cover: analysisResult.entity_cover || '',
    entity_type: getUrlCollectionPrimaryEntityType(analysisResult?.entity_type),
    entity_id: analysisResult.entity_id || '',
    data: stringifyUrlCollectionData({
      entity_name: analysisResult.entity_name || '',
      entity_cover: analysisResult.entity_cover || '',
      page_name: formatShortcutPageName(analysisResult.page_name || ''),
      page_path: analysisResult.page_path || '',
    }),
    order: draftShortcuts.length + 1,
  };
};

const useShortcutImport = (options: IUseShortcutImportOptions): IUseShortcutImportReturn => {
  const { draftShortcuts, maxCount, onDraftShortcutsChange, onImportConfirmed } = options;
  const { formatMessage } = useIntl();
  const [url, setUrl] = useState('');
  const [pageName, setPageName] = useState('');
  const [analysisResult, setAnalysisResult] = useState<IAnalysisUrlInfoResult | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [shortcutImportErrorText, setShortcutImportErrorText] = useState('');
  const importSeqRef = useRef(0);
  const importLoadingRef = useRef(false);
  const confirmLoadingRef = useRef(false);

  const getAnalysisFailureText = (result: IAnalysisUrlInfoResult): string => {
    if (
      result.failure_reason === URL_COLLECTION_ANALYSIS_FAILURE_REASON.InvalidUrl ||
      result.failure_reason === URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedUrl
    ) {
      return formatMessage({
        id: 'dbh_url_collection_analyse_modal_invalid',
        defaultMessage: 'Only DataBrain URLs can be imported. Please check the link and try again.',
      });
    }

    if (result.failure_reason === URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedSystem) {
      return formatMessage({
        id: 'dbh_url_collection_analyse_modal_unsupported_system',
        defaultMessage: 'This DataBrain system is not supported for shortcuts yet.',
      });
    }

    if (result.failure_reason === URL_COLLECTION_ANALYSIS_FAILURE_REASON.HandlerError) {
      return formatMessage({
        id: 'dbh_url_collection_analyse_failed',
        defaultMessage: 'Failed to analyse this URL. Please try again.',
      });
    }

    if (result.permission === Permission.NoPermission) {
      return formatMessage({
        id: 'dbh_url_collection_analyse_modal_no_permission',
        defaultMessage: 'Unable to add this shortcut because you do not have permission to access this URL.',
      });
    }

    if (result.status === Status.Unavailable) {
      return formatMessage({
        id: 'dbh_url_collection_analyse_modal_unavailable',
        defaultMessage: 'This page is unavailable, or the linked entity is no longer available.',
      });
    }

    return formatMessage({
      id: 'dbh_url_collection_analyse_failed',
      defaultMessage: 'Failed to analyse this URL. Please try again.',
    });
  };

  const getDuplicateNameText = (): string => {
    return formatMessage({
      id: 'dbh_url_collection_duplicate_name',
      defaultMessage: 'This name has already been used.',
    });
  };

  const getNameTooLongText = (): string => {
    return formatMessage({
      id: 'dbh_url_collection_name_too_long',
      defaultMessage: 'Name must be 80 characters or fewer.',
    });
  };

  const resetImportState = (): void => {
    importSeqRef.current += 1;
    importLoadingRef.current = false;
    confirmLoadingRef.current = false;
    setUrl('');
    setPageName('');
    setAnalysisResult(null);
    setImportLoading(false);
    setConfirmLoading(false);
    setShortcutImportErrorText('');
  };

  const handleImport = async (): Promise<void> => {
    const nextUrl = url.trim();

    if (!nextUrl) {
      setShortcutImportErrorText(
        formatMessage({
          id: 'dbh_url_collection_url_required',
          defaultMessage: 'Please enter a DataBrain page URL.',
        }),
      );
      return;
    }

    if (importLoadingRef.current) {
      return;
    }

    importLoadingRef.current = true;
    setImportLoading(true);
    setAnalysisResult(null);
    setShortcutImportErrorText('');
    const currentSeq = importSeqRef.current + 1;
    importSeqRef.current = currentSeq;

    try {
      const nextAnalysisResult = await analysisUrlInfo({
        url: nextUrl,
      });
      const nextPageName = formatShortcutPageName(nextAnalysisResult.page_name || '');

      if (importSeqRef.current !== currentSeq) {
        return;
      }

      setAnalysisResult(nextAnalysisResult);
      setPageName(nextPageName);

      if (nextAnalysisResult.success) {
        const nextTargetUrl = nextAnalysisResult.page_url || nextAnalysisResult.page_path;
        const duplicateUrlKey = getDuplicateShortcutUrlKey(draftShortcuts, nextTargetUrl);

        if (duplicateUrlKey) {
          const failureText = formatMessage({
            id: 'dbh_url_collection_duplicate_url',
            defaultMessage: 'This page has already been added.',
          });

          setAnalysisResult({
            ...nextAnalysisResult,
            success: false,
          });
          setPageName('');
          setShortcutImportErrorText(failureText);
          showShortcutNotification('error', failureText);
          return;
        }

        setShortcutImportErrorText('');
        showShortcutNotification(
          'success',
          formatMessage({
            id: 'dbh_url_collection_analyse_modal_identify_success',
            defaultMessage: 'Identified Successfully',
          }),
        );
        return;
      }

      const failureText = getAnalysisFailureText(nextAnalysisResult);
      setShortcutImportErrorText(failureText);
      showShortcutNotification('error', failureText);
    } catch (error) {
      if (importSeqRef.current !== currentSeq) {
        return;
      }

      setAnalysisResult(null);
      setPageName('');
      const failureText = formatMessage({
        id: 'dbh_url_collection_analyse_failed',
        defaultMessage: 'Failed to analyse this URL. Please try again.',
      });

      setShortcutImportErrorText(failureText);
      showShortcutNotification('error', failureText);
    } finally {
      importLoadingRef.current = false;

      if (importSeqRef.current === currentSeq) {
        setImportLoading(false);
      }
    }
  };

  const handleAnalyseConfirm = async (): Promise<void> => {
    const nextName = pageName.trim();

    if (!analysisResult?.success) {
      return;
    }

    if (confirmLoadingRef.current) {
      return;
    }

    if (!nextName) {
      setShortcutImportErrorText(
        formatMessage({
          id: 'dbh_url_collection_name_required',
          defaultMessage: 'Name is required.',
        }),
      );
      return;
    }

    if (draftShortcuts.length >= maxCount) {
      setShortcutImportErrorText(
        formatMessage({
          id: 'dbh_url_collection_max_count_message',
          defaultMessage: 'Maximum limit of 6 favorites reached. Remove an item to add a new one.',
        }),
      );
      return;
    }

    if (nextName.length > SHORTCUT_NAME_MAX_LENGTH) {
      setShortcutImportErrorText(getNameTooLongText());
      return;
    }

    const nextPagePath = analysisResult.page_path || '';

    if (!nextPagePath) {
      setShortcutImportErrorText(
        formatMessage({
          id: 'dbh_url_collection_analyse_modal_invalid',
          defaultMessage: 'Only DataBrain URLs can be imported. Please check the link and try again.',
        }),
      );
      return;
    }

    const nextTargetUrl = analysisResult.page_url || nextPagePath;
    const duplicateUrlKey = getDuplicateShortcutUrlKey(draftShortcuts, nextTargetUrl);

    if (duplicateUrlKey) {
      setShortcutImportErrorText(
        formatMessage({
          id: 'dbh_url_collection_duplicate_url',
          defaultMessage: 'This page has already been added.',
        }),
      );
      return;
    }

    const duplicateNameKey = getDuplicateShortcutNameKey(draftShortcuts, nextName);

    if (duplicateNameKey) {
      setShortcutImportErrorText(getDuplicateNameText());
      return;
    }

    const systemCode = normalizeUrlCollectionSystemCode(
      analysisResult.system || analysisResult.page_path || analysisResult.page_url,
    );

    if (!systemCode) {
      setShortcutImportErrorText(
        formatMessage({
          id: 'dbh_url_collection_invalid_system',
          defaultMessage: 'Unsupported system code.',
        }),
      );
      return;
    }

    confirmLoadingRef.current = true;
    setConfirmLoading(true);

    try {
      const nextItems = reindexUrlCollectionItems([
        ...draftShortcuts,
        createShortcutDraftItem(draftShortcuts, nextName, analysisResult, systemCode),
      ]);

      onDraftShortcutsChange(nextItems);
      onImportConfirmed();
      logUrlCollectionButtonClick(URL_COLLECTION_LOG_CONFIG.addShortcut, {
        name: nextName,
        url: analysisResult.page_url || nextPagePath,
        system: systemCode,
      });
      resetImportState();
    } finally {
      confirmLoadingRef.current = false;
      setConfirmLoading(false);
    }
  };

  const handleUrlChange = (value: string): void => {
    importSeqRef.current += 1;
    setUrl(value);
    setAnalysisResult(null);
    setPageName('');
    setShortcutImportErrorText('');
  };

  const handlePageNameChange = (value: string): void => {
    setPageName(value);
    if (analysisResult?.success) {
      setShortcutImportErrorText('');
    }
  };

  return {
    url,
    pageName,
    analysisResult,
    importLoading,
    confirmLoading,
    shortcutImportErrorText,
    resetImportState,
    handleImport,
    handleAnalyseConfirm,
    handleUrlChange,
    handlePageNameChange,
  };
};

export default useShortcutImport;
